import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AnalyticsService } from 'app/analytics/analytics.service';
import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { ChartTypes } from 'app/shared/model/charts/chart-types.enum';
import { ChartUtilsService } from 'app/shared/service/chart-utils.service';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { TeamService } from 'app/shared/service/team.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

import { ChartsCreationWizardComponent } from '../charts-creation-wizard/charts-creation-wizard.component';

@Component({
  selector: 'cc-charts-grid',
  templateUrl: './charts-grid.component.html',
  styleUrls: ['./charts-grid.component.scss'],
})
export class ChartsGridComponent implements OnInit, OnDestroy {
  readonly canAdd$ = new BehaviorSubject<boolean>(false);
  readonly unSubscribe = new Subject<void>();

  teamId;
  companyId;
  dashboardId;
  charts$: Observable<ChartSettings[]>;
  selectedCharts = new Map();
  dashboard$: Observable<{ name: string }>;
  chartTypes = ChartTypes;

  constructor(
    protected readonly routeParamsService: RouteParamsService,
    protected readonly analyticsService: AnalyticsService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly confirmService: ConfirmService,
    protected readonly toastrService: ToastrService,
    protected readonly errorHandler: ErrorHandler,
    protected readonly userService: UserService,
    protected readonly teamService: TeamService,
    protected readonly modalService: NgbModal,
    public readonly chartUtilsService: ChartUtilsService,
  ) {}

  ngOnInit() {
    this.teamId = this.route.snapshot.paramMap.get('teamId');
    this.companyId = this.route.snapshot.paramMap.get('companyId');
    this.dashboardId = this.route.snapshot.paramMap.get('dashboardId');

    // The team permissions might not be immediately available at the load time
    this.teamService
      .getCurrentTeam$()
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(() => {
        this.canAdd$.next(this.userService.isTeamControllerOrAbove());
      });

    this.charts$ = this.routeParamsService.get('charts').pipe(takeUntil(this.unSubscribe));
    this.dashboard$ = this.routeParamsService.get('dashboard').pipe(takeUntil(this.unSubscribe));
  }

  deleteSelectedCharts() {
    this.confirmService
      .confirmDelete('deleteCharts')
      .then(async () => {
        try {
          const promises = [];
          this.selectedCharts.forEach((chart, id) => {
            let promise;
            if (this.teamId) {
              promise = this.analyticsService.deleteTeamChart(this.teamId, this.dashboardId, id).toPromise();
            }
            if (this.companyId) {
              promise = this.analyticsService
                .deleteCompanyChart(this.companyId, this.dashboardId, id)
                .toPromise();
            }
            promises.push(promise);
          });
          await Promise.all(promises);
          this.toastrService.successByKey('chartsDeleted');
        } catch (e) {
          this.errorHandler.handle(e);
        }
        this.clearSelection();
        void this.router.navigate([], { relativeTo: this.route, state: { reload: true } });
        return true;
      })
      .catch(() => {
        // no-op
      });
  }

  async addChart() {
    const modal = this.modalService.open(ChartsCreationWizardComponent, { size: 'lg' });
    modal.componentInstance.teamId = this.teamId;
    modal.componentInstance.companyId = this.companyId;
    modal.componentInstance.dashboardId = this.dashboardId;

    try {
      const chart = await modal.result;
      this.onChartClick(chart._id);
    } catch (error) {
      if (error) {
        this.errorHandler.handle(error);
      }
    }
  }

  trackBy(index: number, chart: ChartSettings) {
    return chart._id;
  }

  clearSelection() {
    this.selectedCharts.clear();
  }

  onChartClick(chartId) {
    void this.router.navigate([chartId], { relativeTo: this.route });
  }

  onSelectionToggle(chart, selected) {
    if (selected) {
      this.selectedCharts.set(chart._id, chart);
    } else {
      this.selectedCharts.delete(chart._id);
    }
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
